import { Trans } from 'react-i18next';
import { IntegrationHowItWorks, IntegrationWorkflowsHowItWorks } from './types';
import { IS_SANDBOX_ENVIRONMENT, VOPAY_API_ENDPOINT_URL } from './vopayApiEndpoint';

/**
 * "How it works" copy per integration, from the Figma "<Integration> - Instructions" frames. Kept out
 * of the catalogue because identifiers are emphasised and two Plaid steps link to Plaid's docs
 * mid-sentence, which needs <Trans>. Copy resolves against a root t with full keys (see ./types.ts).
 */

// `link` is an HTML void element, so a <link>…</link> placeholder is parsed as empty — hence docsLink
const EMPHASIS = { strong: <strong />, em: <em /> };

const PLAID_DOCS = {
  exchangePublicToken: 'https://plaid.com/docs/api/items/#itempublic_tokenexchange',
  createProcessorToken: 'https://plaid.com/docs/api/processors/#processortokencreate',
};

const FLINKS = 'Integrations.Catalogue.Flinks.Setup';
const INVERITE = 'Integrations.Catalogue.Inverite.Setup';
const GLOBAL_PAYMENTS = 'Integrations.Catalogue.GlobalPayments.Setup';
const PLAID = 'Integrations.Catalogue.Plaid.Setup';

export const FLINKS_HOW_IT_WORKS: IntegrationHowItWorks = {
  kind: 'steps',
  intro: (t) =>
    t(`${FLINKS}.intro`, {
      defaultValue:
        'In order to enable this integration you must provide us your own Flinks API URL, which typically looks like this:',
    }),
  example: 'https://{instance}-api.private.fin.ag/v3/{customerId}',
  steps: [
    (t) =>
      t(`${FLINKS}.Steps.navigateToFlinksLogin`, {
        defaultValue: 'Navigate user to the Flinks login screen, typically using an embedded iFrame.',
      }),
    (t) =>
      t(`${FLINKS}.Steps.selectFinancialInstitution`, {
        defaultValue: 'User selects their preferred financial institution from the iFrame.',
      }),
    (t) => t(`${FLINKS}.Steps.userLoginBankAccount`, { defaultValue: 'User logs in to their online banking account.' }),
    (t) => t(`${FLINKS}.Steps.selectBankAccount`, { defaultValue: 'User selects the account they wish to use.' }),
    (t) =>
      t(`${FLINKS}.Steps.redirectWithLoginAndAccountId`, {
        defaultValue: 'Flinks redirects the user back to your website, and provides the login ID and Account ID.',
      }),
    (t) => (
      <Trans
        t={t}
        i18nKey={`${FLINKS}.Steps.apiCallWithFlinksIds`}
        defaults="You make an API call to the desired EFT endpoint, providing <em>FlinksAccountID</em> and <em>FlinksLoginID</em> instead of account number, transit number, and financial institution number."
        components={EMPHASIS}
      />
    ),
    (t) =>
      t(`${FLINKS}.Steps.fetchAccountDetailsForEft`, {
        defaultValue:
          'iQ11 fetches the account number, transit number, and financial institution number for the specified Flinks Account ID and uses that to process the EFT transaction.',
      }),
  ],
};

export const INVERITE_HOW_IT_WORKS: IntegrationHowItWorks = {
  kind: 'steps',
  intro: (t) =>
    IS_SANDBOX_ENVIRONMENT
      ? t(`${INVERITE}.intro`, { defaultValue: 'VoPay Sandbox API Endpoint URL' })
      : t(`${INVERITE}.introProduction`, { defaultValue: 'VoPay API Endpoint URL' }),
  example: VOPAY_API_ENDPOINT_URL,
  steps: [
    (t) => (
      <Trans
        t={t}
        i18nKey={`${INVERITE}.Steps.stepCreateRequestGuid`}
        defaults="Use a Create API from Inverite in order to generate an <strong>InveriteRequestGUID</strong> and iframe url."
        components={EMPHASIS}
      />
    ),
    (t) =>
      t(`${INVERITE}.Steps.stepNavigateLogin`, {
        defaultValue: 'Navigate user to the Inverite login screen, typically using your Inverite iFrame.',
      }),
    (t) =>
      t(`${INVERITE}.Steps.stepSelectInstitution`, {
        defaultValue: 'User selects their preferred financial institution from the iFrame.',
      }),
    (t) => t(`${INVERITE}.Steps.stepLoginBank`, { defaultValue: 'User logs in to their online banking account.' }),
    (t) => t(`${INVERITE}.Steps.stepSelectAccount`, { defaultValue: 'User selects the account they wish to use.' }),
    (t) => (
      <Trans
        t={t}
        i18nKey={`${INVERITE}.Steps.stepCallApiWithGuid`}
        defaults="After that, you make an API call to the desired VoPay EFT endpoint, providing the <em>InveriteRequestGUID</em> instead of account number, transit number, and financial institution number."
        components={EMPHASIS}
      />
    ),
    (t) =>
      t(`${INVERITE}.Steps.stepFetchAccountDetails`, {
        defaultValue:
          'iQ11 fetches the account number, transit number, and financial institution number for the EFT enabled bank account and uses that to process the EFT transaction.',
      }),
  ],
};

export const GLOBAL_PAYMENTS_HOW_IT_WORKS: IntegrationHowItWorks = {
  kind: 'steps',
  intro: (t) =>
    t(`${GLOBAL_PAYMENTS}.intro`, {
      defaultValue:
        "Global Payments should provide an App ID and Shared Secret that uniquely identify your merchant account. Once your VoPay account is configured, credit card transactions captured through iQ11 are tokenized against your Global Payments merchant account instead of VoPay's.",
    }),
  steps: [
    (t) =>
      t(`${GLOBAL_PAYMENTS}.Steps.customerEntersCard`, {
        defaultValue: 'Your customer enters their card details in the iQ11 credit card form.',
      }),
    (t) =>
      t(`${GLOBAL_PAYMENTS}.Steps.sendCardDetails`, {
        defaultValue: 'iQ11 sends the card details to Global Payments using your App ID and Shared Secret.',
      }),
    (t) =>
      t(`${GLOBAL_PAYMENTS}.Steps.processWithToken`, {
        defaultValue: 'iQ11 uses that token to process the credit card transaction.',
      }),
    (t) =>
      t(`${GLOBAL_PAYMENTS}.Steps.settlement`, {
        defaultValue:
          'The transaction settles to your Global Payments merchant account, and appears in your VoPay transaction history.',
      }),
  ],
};

/** Plaid has two ways to hand iQ11 a bank account — the drawer's "How it works" lets the client pick one */
export const PLAID_HOW_IT_WORKS: IntegrationWorkflowsHowItWorks = {
  kind: 'workflows',
  intro: (t) =>
    t(`${PLAID}.intro`, {
      defaultValue:
        'There are two typical workflows for a Plaid-enabled integration with iQ11, detailed in the workflows below:',
    }),
  workflows: [
    {
      id: 'publicToken',
      number: 1,
      name: (t) => t(`${PLAID}.Workflows.PublicToken.name`, { defaultValue: 'Public Token' }),
      steps: [
        (t) =>
          t(`${PLAID}.Workflows.PublicToken.Steps.plaidNavigateLogin`, {
            defaultValue: 'Users navigate to the Plaid login screen, typically using your Plaid iFrame.',
          }),
        (t) =>
          t(`${PLAID}.Workflows.PublicToken.Steps.plaidSelectInstitution`, {
            defaultValue: 'Users select their preferred financial institution from the iFrame.',
          }),
        (t) =>
          t(`${PLAID}.Workflows.PublicToken.Steps.plaidLoginBankAccount`, {
            defaultValue: 'Users login to their online banking account.',
          }),
        (t) =>
          t(`${PLAID}.Workflows.PublicToken.Steps.plaidSelectAccount`, {
            defaultValue: 'Users select the account they wish to use.',
          }),
        (t) =>
          t(`${PLAID}.Workflows.PublicToken.Steps.plaidReturnTokenAndId`, {
            defaultValue: 'Plaid returns back the Plaid Public Token and Account ID.',
          }),
        (t) => (
          <Trans
            t={t}
            i18nKey={`${PLAID}.Workflows.PublicToken.Steps.plaidApiCallWithTokenAndId`}
            defaults="Within 30 minutes you make an API call to the desired VoPay EFT endpoint, providing the <em>PlaidPublicToken</em> and <em>PlaidAccountID</em> instead of account number, transit number, and financial institution number."
            components={EMPHASIS}
          />
        ),
        (t) =>
          t(`${PLAID}.Workflows.PublicToken.Steps.plaidFetchAccountDetails`, {
            defaultValue:
              'iQ11 fetches the account number, transit number, and financial institution number for the specified Plaid Account ID and uses that to process the EFT transaction.',
          }),
      ],
    },
    {
      id: 'processorToken',
      number: 2,
      name: (t) => t(`${PLAID}.Workflows.ProcessorToken.name`, { defaultValue: 'Processor Token' }),
      intro: (t) =>
        t(`${PLAID}.Workflows.ProcessorToken.intro`, {
          defaultValue:
            'With this approach, you are responsible for exchanging the Public Token for an Access Token. When you wish to initiate a transaction, you will call Plaid’s API to create a Processor Token from your Access Token.',
        }),
      steps: [
        (t) => (
          <Trans
            t={t}
            i18nKey={`${PLAID}.Workflows.ProcessorToken.Steps.exchangeTokenStep`}
            defaults="Within 30 minutes you exchange the Plaid Public Token you received for an Access Token by calling Plaid’s endpoint /item/public_token/exchange. Please visit the link <docsLink>here</docsLink>."
            components={{ docsLink: <a href={PLAID_DOCS.exchangePublicToken} target="_blank" rel="noreferrer" /> }}
          />
        ),
        (t) => (
          <Trans
            t={t}
            i18nKey={`${PLAID}.Workflows.ProcessorToken.Steps.createProcessorTokenStep`}
            defaults="Once you successfully receive the AccessToken you need to generate the Processor Token by calling Plaid's endpoint processor/token/create. Please visit the link <docsLink>here</docsLink>."
            components={{ docsLink: <a href={PLAID_DOCS.createProcessorToken} target="_blank" rel="noreferrer" /> }}
          />
        ),
        (t) => (
          <Trans
            t={t}
            i18nKey={`${PLAID}.Workflows.ProcessorToken.Steps.apiCallWithProcessorToken`}
            defaults="You make an API call to the desired EFT endpoint, providing the <em>PlaidProcessorToken</em> instead of the account number, transit number, and financial institution number."
            components={EMPHASIS}
          />
        ),
        (t) =>
          t(`${PLAID}.Workflows.ProcessorToken.Steps.fetchDetailsFromProcessorToken`, {
            defaultValue:
              'iQ11 fetches the account number, transit number, and financial institution number for the specified Plaid Processor token and uses that to process the EFT transaction.',
          }),
      ],
    },
  ],
};
